import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import Popup from '../components/Popup';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .cl-page {
    font-family: 'Inter', system-ui, sans-serif;
    min-height: 100vh;
    background: #0a0f1e;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 32px 20px 60px;
    position: relative;
    overflow: hidden;
  }

  /* ambient blobs */
  .cl-blob {
    position: fixed;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    z-index: 0;
  }
  .cl-blob-1 { width: 600px; height: 600px; background: rgba(99,102,241,0.1);  top: -200px; left: -200px; }
  .cl-blob-2 { width: 500px; height: 500px; background: rgba(79,70,229,0.08);  bottom: -150px; right: -150px; }

  .cl-card {
    position: relative;
    z-index: 1;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 24px;
    padding: 44px 40px 40px;
    width: 100%;
    max-width: 820px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.4);
    backdrop-filter: blur(18px);
    animation: clFadeUp 0.45s ease-out both;
  }

  @keyframes clFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── header ── */
  .cl-logo-img {
    height: 44px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    border-radius: 12px;
    filter: drop-shadow(0 0 20px rgba(99,102,241,0.5));
    margin-bottom: 16px;
  }
  .cl-header { margin-bottom: 28px; }
  .cl-title    { font-size: 1.75rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.03em; margin: 0 0 4px; }
  .cl-subtitle { font-size: 14px; color: #64748b; margin: 0; }

  /* ── alerts ── */
  .cl-alert { border-radius: 12px; padding: 14px 16px; font-size: 14px; margin-bottom: 22px; line-height: 1.55; }
  .cl-alert-error   { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }
  .cl-alert-success { background: rgba(34,197,94,0.1);  border: 1px solid rgba(34,197,94,0.2);   color: #86efac; }

  .cl-copy-btn {
    display: inline-flex; align-items: center; gap: 5px;
    background: rgba(34,197,94,0.12);
    border: 1px solid rgba(34,197,94,0.25);
    color: #86efac;
    padding: 5px 12px;
    border-radius: 7px;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    margin-left: 8px;
    transition: all 0.2s;
  }
  .cl-copy-btn:hover { background: rgba(34,197,94,0.2); }
  .cl-short-url { color: #a5b4fc; font-weight: 600; word-break: break-all; }

  /* ── tab bar ── */
  .cl-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 28px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 14px;
    padding: 5px;
  }
  .cl-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 10px 16px;
    border-radius: 10px;
    border: none;
    background: transparent;
    color: #64748b;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.22s;
    position: relative;
  }
  .cl-tab:hover:not(.cl-tab-active) {
    color: #94a3b8;
    background: rgba(99,102,241,0.06);
  }
  .cl-tab-active {
    background: linear-gradient(135deg, rgba(99,102,241,0.25), rgba(79,70,229,0.18));
    color: #c7d2fe;
    border: 1px solid rgba(99,102,241,0.3);
    box-shadow: 0 2px 12px rgba(99,102,241,0.15);
  }
  .cl-tab-icon { font-size: 16px; }
  .cl-tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #6366f1;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    line-height: 1;
  }

  /* ── two-column grid (Essentials tab) ── */
  .cl-two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    align-items: start;
  }
  .cl-col-divider {
    border: none;
    border-left: 1px solid rgba(99,102,241,0.1);
    margin: 0;
    grid-row: span 99;
    align-self: stretch;
    width: 1px;
    display: block;
  }
  @media (max-width: 600px) {
    .cl-two-col {
      grid-template-columns: 1fr;
    }
    .cl-col-divider { display: none; }
    .cl-card { padding: 28px 18px 28px; }
  }

  /* ── Options section cards ── */
  .cl-opt-cards { display: flex; flex-direction: column; gap: 16px; }
  .cl-opt-card {
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 14px;
    padding: 20px 20px 18px;
    transition: border-color 0.2s;
  }
  .cl-opt-card:hover { border-color: rgba(99,102,241,0.22); }
  .cl-opt-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .cl-opt-card-icon { font-size: 18px; }
  .cl-opt-card-title { font-size: 14px; font-weight: 700; color: #e2e8f0; }
  .cl-opt-card-desc  { font-size: 12px; color: #475569; margin-left: auto; }
  .cl-opt-hint { font-size: 12px; color: #475569; margin-top: 8px; }
  .cl-opt-hint a { color: #818cf8; text-decoration: none; }
  .cl-opt-hint a:hover { text-decoration: underline; }

  /* ── section label used inside URL Structure ── */
  .cl-section-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #475569;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* ── form group ── */
  .cl-form-group { display: flex; flex-direction: column; gap: 0; margin-bottom: 18px; }
  .cl-form-group:last-child { margin-bottom: 0; }

  .cl-label { font-size: 13px; font-weight: 700; color: #94a3b8; margin-bottom: 6px; display: block; }

  .cl-label-row {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 6px;
  }
  .cl-pro-tag {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
    color: #fbbf24;
    background: rgba(251,191,36,0.1);
    border: 1px solid rgba(251,191,36,0.2);
    padding: 2px 8px;
    border-radius: 100px;
  }

  .cl-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
    color: #e2e8f0;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    width: 100%;
    box-sizing: border-box;
  }
  .cl-input::placeholder { color: #334155; }
  .cl-input:focus { border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.05); }
  .cl-input:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: rgba(255,255,255,0.02);
  }
  .cl-input option { background: #0f172a; color: #e2e8f0; }

  /* availability indicator */
  .cl-avail {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; font-weight: 600; margin-top: 6px;
  }
  .cl-avail-ok   { color: #86efac; }
  .cl-avail-no   { color: #fca5a5; }
  .cl-avail-wait { color: #475569; }

  /* ── url‑type selector ── */
  .cl-url-type-options { display: flex; flex-direction: column; gap: 8px; }
  .cl-url-type-option {
    display: flex; align-items: flex-start; gap: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.15);
    border-radius: 10px; padding: 10px 12px;
    cursor: pointer; transition: all 0.2s;
  }
  .cl-url-type-option:hover { border-color: rgba(99,102,241,0.35); background: rgba(99,102,241,0.06); }
  .cl-url-type-option.selected { border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.08); }
  .cl-url-type-option.disabled { opacity: 0.4; cursor: not-allowed; }
  .cl-url-type-option.disabled:hover { border-color: rgba(99,102,241,0.15); background: rgba(255,255,255,0.03); }
  .cl-url-type-radio { width: 15px; height: 15px; accent-color: #6366f1; margin-top: 2px; flex-shrink: 0; }
  .cl-url-type-name    { font-size: 13px; font-weight: 700; color: #e2e8f0; }
  .cl-url-type-preview { font-size: 11.5px; color: #6366f1; margin-top: 2px; }
  .cl-url-type-preview .brand-part { color: #fbbf24; }

  /* ── live preview box ── */
  .cl-preview-box {
    background: rgba(99,102,241,0.06);
    border: 1px solid rgba(99,102,241,0.15);
    border-radius: 14px;
    padding: 16px 20px;
    margin-top: 24px;
    text-align: center;
  }
  .cl-preview-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 6px;
  }
  .cl-preview-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; }
  .cl-regen-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 20px;
    border: 1px solid rgba(99,102,241,0.3);
    background: rgba(99,102,241,0.1);
    color: #a5b4fc;
    font-size: 11px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.18s;
    letter-spacing: 0.03em;
  }
  .cl-regen-btn:hover {
    background: rgba(99,102,241,0.22);
    border-color: rgba(99,102,241,0.5);
    color: #c7d2fe;
    transform: rotate(-18deg) scale(1.08);
  }
  .cl-preview-url   { font-size: 17px; color: #818cf8; font-weight: 600; word-break: break-all; }
  .cl-preview-url .brand-part { color: #fbbf24; }
  .cl-preview-url .slug-part  { color: #c4b5fd; }

  /* ── free-plan note in URL Structure right col ── */
  .cl-free-note {
    background: rgba(99,102,241,0.05);
    border: 1px dashed rgba(99,102,241,0.2);
    border-radius: 12px;
    padding: 20px 16px;
    text-align: center;
  }
  .cl-free-note-icon { font-size: 28px; margin-bottom: 8px; }
  .cl-free-note-title { font-size: 13px; font-weight: 700; color: #94a3b8; margin-bottom: 4px; }
  .cl-free-note-sub { font-size: 12px; color: #475569; }
  .cl-free-note-sub a { color: #818cf8; text-decoration: none; font-weight: 600; }
  .cl-free-note-sub a:hover { text-decoration: underline; }

  /* ── biz tag ── */
  .cl-biz-tag {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
    color: #fbbf24;
    background: rgba(251,191,36,0.1);
    border: 1px solid rgba(251,191,36,0.2);
    padding: 2px 7px; border-radius: 100px;
  }

  /* ── Help button ── */
  .cl-help-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 100px;
    border: 1px solid rgba(99,102,241,0.3);
    background: rgba(99,102,241,0.08);
    color: #a5b4fc;
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.01em;
  }
  .cl-help-btn:hover {
    background: rgba(99,102,241,0.18);
    border-color: rgba(99,102,241,0.55);
    color: #c7d2fe;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(99,102,241,0.2);
  }
  .cl-help-btn-icon {
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(99,102,241,0.25);
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 900;
    flex-shrink: 0;
    border: 1.5px solid rgba(99,102,241,0.4);
  }

  /* ── Help modal ── */
  .cl-help-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    z-index: 2000; padding: 20px;
    animation: clFadeUp 0.2s ease-out both;
  }
  .cl-help-modal {
    background: #0f172a;
    border: 1px solid rgba(99,102,241,0.28);
    border-radius: 24px;
    padding: 36px 32px 32px;
    width: 100%; max-width: 560px;
    max-height: 88vh;
    overflow-y: auto;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    position: relative;
  }
  .cl-help-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px;
  }
  .cl-help-modal-title {
    font-size: 1.2rem; font-weight: 900; color: #f1f5f9; margin: 0;
    display: flex; align-items: center; gap: 10px;
  }
  .cl-help-close {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: #64748b; border-radius: 9px;
    width: 32px; height: 32px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 16px; cursor: pointer; font-family: inherit;
    transition: all 0.2s; flex-shrink: 0;
  }
  .cl-help-close:hover { background: rgba(239,68,68,0.12); color: #fca5a5; border-color: rgba(239,68,68,0.25); }

  .cl-help-rows { display: flex; flex-direction: column; gap: 4px; }
  .cl-help-row {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(99,102,241,0.1);
    transition: border-color 0.18s;
  }
  .cl-help-row:hover { border-color: rgba(99,102,241,0.22); }
  .cl-help-row-icon { font-size: 20px; line-height: 1; flex-shrink: 0; margin-top: 1px; }
  .cl-help-row-title { font-size: 13px; font-weight: 800; color: #e2e8f0; margin-bottom: 3px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .cl-help-row-desc  { font-size: 12.5px; color: #64748b; line-height: 1.6; }
  .cl-help-row-desc code { color: #a5b4fc; background: rgba(99,102,241,0.1); padding: 1px 5px; border-radius: 4px; font-size: 11.5px; }
  .cl-help-pro-tag {
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
    color: #fbbf24; background: rgba(251,191,36,0.1); border: 1px solid rgba(251,191,36,0.22);
    padding: 1px 6px; border-radius: 100px;
  }


  /* ── submit & back ── */
  .cl-actions { margin-top: 28px; }
  .cl-submit {
    width: 100%;
    padding: 14px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35);
    margin-bottom: 10px;
  }
  .cl-submit:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(99,102,241,0.5); }
  .cl-submit:active { transform: translateY(0); }

  .cl-back {
    width: 100%;
    padding: 12px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(99,102,241,0.18);
    color: #64748b;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.2s;
  }
  .cl-back:hover { background: rgba(99,102,241,0.1); color: #a5b4fc; border-color: rgba(99,102,241,0.3); }

  /* ── no-brand warning ── */
  .cl-nobrand-warn {
    background: rgba(239,68,68,0.07);
    border: 1px solid rgba(239,68,68,0.18);
    border-radius: 10px;
    padding: 10px 12px;
    font-size: 12.5px;
    color: #fca5a5;
    margin-bottom: 10px;
  }
  .cl-nobrand-warn a { color: #fbbf24; text-decoration: none; font-weight: 600; }
`;

/* ─── component ──────────────────────────────────────────────────────────── */
const CreateLink = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'confirm', onConfirm: null });
  const [helpOpen, setHelpOpen] = useState(false);

  // Generate a random 6-char hex code (same format as backend)
  const generateCode = () => Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');

  const [pregenCode, setPregenCode] = useState(() => generateCode());
  const [longUrl, setLongUrl] = useState('');
  const [customShortCode, setCustomShortCode] = useState('');
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [urlType, setUrlType] = useState('standard');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [codeStatus, setCodeStatus] = useState(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const [activeTab, setActiveTab] = useState('essentials');
  const checkTimer = useRef(null);

  const isFree = user?.plan === 'free';
  const isPro = user?.plan === 'pro';
  const isBusiness = user?.plan === 'business';
  const canUseBrand = isPro || isBusiness;

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');

  useEffect(() => {
    if (canUseBrand) {
      axios.get(`${API_BASE_URL}/api/brand`)
        .then(r => {
          const list = r.data.brands || [];
          setBrands(list);
          if (list.length > 0) setSelectedBrand(list[0].brand_name);
        })
        .catch(() => { });
    }
  }, [canUseBrand]);

  // Debounced short code availability check
  useEffect(() => {
    setCodeStatus(null);
    if (checkTimer.current) clearTimeout(checkTimer.current);
    const code = customShortCode.trim();
    if (!code || code.length < 3 || isFree) return;

    setCodeChecking(true);
    checkTimer.current = setTimeout(async () => {
      try {
        const queryParams = `?urlType=${urlType}${urlType !== 'standard' && selectedBrand ? `&brandName=${encodeURIComponent(selectedBrand)}` : ''}`;
        const r = await axios.get(`${API_BASE_URL}/api/links/check/${encodeURIComponent(code)}${queryParams}`);
        setCodeStatus(r.data);
      } catch {
        setCodeStatus(null);
      } finally {
        setCodeChecking(false);
      }
    }, 400);

    return () => { if (checkTimer.current) clearTimeout(checkTimer.current); };
  }, [customShortCode, isFree, urlType]);

  const copyToClipboard = async (url) => {
    try { await navigator.clipboard.writeText(url); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    setPopup({ isOpen: true, title: 'Copied!', message: 'Short URL copied to clipboard.', type: 'success' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    try {
      const requestData = {
        longUrl,
        customShortCode: customShortCode || undefined,
        pregenCode: !customShortCode ? pregenCode : undefined,
        expiresAt: expiresAt || undefined,
        urlType,
      };
      if (password.trim()) requestData.password = password;
      if (urlType !== 'standard' && selectedBrand) requestData.brandName = selectedBrand;

      const response = await axios.post(`${API_BASE_URL}/api/links/create`, requestData);
      const link = response.data.link;

      let shortUrl;
      const brand = link.brand_name || selectedBrand;
      if (urlType === 'brand_path' && brand) {
        shortUrl = `${API_BASE_URL}/${brand}/${link.short_code}`;
      } else if (urlType === 'brand_subdomain' && brand) {
        const base = API_BASE_URL.replace('://', `://${brand}.`);
        shortUrl = `${base}/${link.short_code}`;
      } else {
        shortUrl = `${API_BASE_URL}/${link.short_code}`;
      }

      setSuccess({ url: shortUrl });
      setLongUrl(''); setCustomShortCode(''); setPassword(''); setExpiresAt(''); setUrlType('standard');
      setActiveTab('essentials');
      setPregenCode(generateCode()); // fresh code for next link
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create link';
      setError(msg.includes('Link limit reached') ? { limitReached: true, msg } : { msg });
    }
  };

  // Slug shown in preview: custom code if typed, otherwise the pre-generated code
  const slugPreview = customShortCode.trim() || pregenCode;
  const activeBrand = selectedBrand || 'yourbrand';
  const getPreviewUrl = (type) => {
    if (type === 'brand_path') return { prefix: 'rdrto.com/', brand: activeBrand, middle: '/', slug: slugPreview };
    if (type === 'brand_subdomain') return { prefix: '', brand: activeBrand, middle: '.rdrto.com/', slug: slugPreview };
    return { prefix: 'rdrto.com/', brand: null, middle: '', slug: slugPreview };
  };

  /* options badge count */
  const optionsFilled = (password.trim() ? 1 : 0) + (expiresAt ? 1 : 0);

  return (
    <div className="cl-page">
      <style>{STYLES}</style>

      <div className="cl-blob cl-blob-1" />
      <div className="cl-blob cl-blob-2" />

      <div className="cl-card">

        {/* Header */}
        <img src={new URL('../assets/images/logo.png', import.meta.url).href} alt="RDRTO Logo" className="cl-logo-img" />
        <div className="cl-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h1 className="cl-title">Create Short Link</h1>
            <p className="cl-subtitle">Transform your long URLs into clean, shareable short links</p>
          </div>
          <button
            type="button"
            className="cl-help-btn"
            onClick={() => setHelpOpen(true)}
            title="How to use this form"
          >
            <span className="cl-help-btn-icon">?</span>
            Help
          </button>
        </div>

        {/* Help Modal */}
        {helpOpen && (
          <div className="cl-help-overlay" onClick={e => e.target === e.currentTarget && setHelpOpen(false)}>
            <div className="cl-help-modal">
              <div className="cl-help-modal-header">
                <h2 className="cl-help-modal-title">Quick Guide</h2>
                <button className="cl-help-close" onClick={() => setHelpOpen(false)}>✕</button>
              </div>

              <div className="cl-help-rows">

                <div className="cl-help-row">
                  <span className="cl-help-row-icon">🔗</span>
                  <div>
                    <div className="cl-help-row-title">Destination URL</div>
                    <div className="cl-help-row-desc">The full URL you want to shorten (must start with <code>https://</code>).</div>
                  </div>
                </div>

                <div className="cl-help-row">
                  <span className="cl-help-row-icon">✏️</span>
                  <div>
                    <div className="cl-help-row-title">Custom Short Code <span className="cl-help-pro-tag">Pro</span></div>
                    <div className="cl-help-row-desc">Pick a custom alias e.g. <code>my-sale</code>. Leave blank for a random code.</div>
                  </div>
                </div>

                <div className="cl-help-row">
                  <span className="cl-help-row-icon">🏷️</span>
                  <div>
                    <div className="cl-help-row-title">URL Structure <span className="cl-help-pro-tag">Pro</span></div>
                    <div className="cl-help-row-desc">
                      <span style={{ color: '#818cf8' }}>rdrto.com/<span style={{ color: '#c4b5fd' }}>code</span></span> · Standard<br />
                      <span style={{ color: '#818cf8' }}>rdrto.com/<span style={{ color: '#fbbf24' }}>brand</span>/<span style={{ color: '#c4b5fd' }}>code</span></span> · Brand in path<br />
                      <span style={{ color: '#fbbf24' }}>brand</span><span style={{ color: '#818cf8' }}>.rdrto.com/<span style={{ color: '#c4b5fd' }}>code</span></span> · Brand subdomain
                    </div>
                  </div>
                </div>

                <div className="cl-help-row">
                  <span className="cl-help-row-icon">🔒</span>
                  <div>
                    <div className="cl-help-row-title">Password Protection <span className="cl-help-pro-tag">Pro</span></div>
                    <div className="cl-help-row-desc">Visitors must enter a password before being redirected.</div>
                  </div>
                </div>

                <div className="cl-help-row">
                  <span className="cl-help-row-icon">📅</span>
                  <div>
                    <div className="cl-help-row-title">Expiration Date <span className="cl-help-pro-tag">Pro</span></div>
                    <div className="cl-help-row-desc">Link automatically deactivates on the chosen date.</div>
                  </div>
                </div>

                <div className="cl-help-row">
                  <span className="cl-help-row-icon">👁️</span>
                  <div>
                    <div className="cl-help-row-title">Link Preview</div>
                    <div className="cl-help-row-desc">See your final link live. Hit <strong>↻ New code</strong> to shuffle the random slug.</div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="cl-alert cl-alert-error">
            {error.msg}
            {error.limitReached && (
              <> <Link to="/upgrade" style={{ color: '#fca5a5', fontWeight: 700 }}>Upgrade now</Link> to create more links.</>
            )}
          </div>
        )}
        {success && (
          <div className="cl-alert cl-alert-success">
            ✅ Link created!{' '}
            <span className="cl-short-url">{success.url}</span>
            <button className="cl-copy-btn" onClick={() => copyToClipboard(success.url)}>📋 Copy</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* ── Tab bar ── */}
          <div className="cl-tabs">
            <button
              type="button"
              id="tab-essentials"
              className={`cl-tab${activeTab === 'essentials' ? ' cl-tab-active' : ''}`}
              onClick={() => setActiveTab('essentials')}
            >
              <span className="cl-tab-icon">🔗</span>
              Essentials
            </button>
            <button
              type="button"
              id="tab-options"
              className={`cl-tab${activeTab === 'options' ? ' cl-tab-active' : ''}`}
              onClick={() => setActiveTab('options')}
            >
              <span className="cl-tab-icon">⚙️</span>
              Options
              {optionsFilled > 0 && (
                <span className="cl-tab-badge">{optionsFilled}</span>
              )}
            </button>
          </div>

          {/* ── Tab 1: Essentials ── */}
          {activeTab === 'essentials' && (
            <div className="cl-two-col">

              {/* Left column */}
              <div>
                {/* Destination URL */}
                <div className="cl-form-group">
                  <label className="cl-label" htmlFor="cl-long-url">Destination URL</label>
                  <input
                    id="cl-long-url"
                    type="url"
                    className="cl-input"
                    placeholder="https://example.com/your-very-long-url"
                    value={longUrl}
                    onChange={e => setLongUrl(e.target.value)}
                    required
                  />
                </div>

                {/* Custom Short Code */}
                <div className="cl-form-group">
                  <div className="cl-label-row">
                    <label className="cl-label" htmlFor="cl-short-code">Custom Short Code</label>
                    {isFree && <span className="cl-pro-tag">⭐ Pro</span>}
                  </div>
                  <input
                    id="cl-short-code"
                    type="text"
                    className="cl-input"
                    placeholder={isFree ? 'Upgrade to use a custom alias' : 'my-custom-link'}
                    value={customShortCode}
                    onChange={e => setCustomShortCode(e.target.value)}
                    disabled={isFree}
                  />
                  {!isFree && customShortCode.trim().length >= 3 && (
                    <div className={`cl-avail ${codeChecking ? 'cl-avail-wait' : codeStatus?.available ? 'cl-avail-ok' : 'cl-avail-no'}`}>
                      {codeChecking ? '⏳ Checking…' : codeStatus?.available ? '✅ Available' : codeStatus ? `❌ ${codeStatus.reason}` : ''}
                    </div>
                  )}
                  {isFree && (
                    <p className="cl-opt-hint">Custom aliases require a Pro plan. <Link to="/upgrade">Upgrade now</Link></p>
                  )}
                </div>
              </div>

              {/* Right column — URL Structure */}
              <div>



                {!canUseBrand ? (
                  /* Free user — show upgrade nudge */
                  <div className="cl-free-note">
                    <div className="cl-free-note-icon">🔗</div>
                    <div className="cl-free-note-title">Branded Links</div>
                    <div className="cl-free-note-sub">
                      Use your own brand name in the URL.<br />
                      <Link to="/upgrade">Upgrade to Pro</Link> to unlock.
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Brand name selector — always shown above URL Structure */}
                    {brands.length > 0 && (
                      <div className="cl-form-group" style={{ marginBottom: 14 }}>
                        <div className="cl-label-row">
                          <label className="cl-label">Use Brand Name</label>
                          <Link to="/brand-settings" style={{ fontSize: '12px', color: '#818cf8', textDecoration: 'none', fontWeight: 600 }}>
                            ⚙️ Manage
                          </Link>
                        </div>
                        <select
                          className="cl-input"
                          value={selectedBrand}
                          onChange={e => setSelectedBrand(e.target.value)}
                        >
                          {brands.map(b => (
                            <option key={b.brand_name} value={b.brand_name}>{b.brand_name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="cl-section-label">
                      URL Structure
                    </div>

                    {/* No brand warning */}
                    {brands.length === 0 && (
                      <div className="cl-nobrand-warn">
                        ⚠️ No brand name set up yet.{' '}
                        <Link to="/brand-settings">Set one in Brand Settings</Link> to use branded URLs.
                      </div>
                    )}

                    <div className="cl-url-type-options">
                      {[
                        { value: 'standard', label: 'Standard' },
                        { value: 'brand_path', label: 'Brand in Path' },
                        { value: 'brand_subdomain', label: 'Brand as Subdomain' },
                      ].map(opt => {
                        const preview = getPreviewUrl(opt.value);
                        const disabled = opt.value !== 'standard' && brands.length === 0;
                        return (
                          <label
                            key={opt.value}
                            className={`cl-url-type-option${urlType === opt.value ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
                          >
                            <input
                              type="radio"
                              className="cl-url-type-radio"
                              name="urlType"
                              value={opt.value}
                              checked={urlType === opt.value}
                              onChange={() => !disabled && setUrlType(opt.value)}
                              disabled={disabled}
                            />
                            <div>
                              <div className="cl-url-type-name">{opt.label}</div>
                              <div className="cl-url-type-preview">
                                {preview.prefix}
                                {preview.brand && <span className="brand-part">{preview.brand}</span>}
                                {preview.middle}<span style={{ color: '#c4b5fd' }}>{preview.slug}</span>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

            </div>
          )}

          {/* ── Tab 2: Options ── */}
          {activeTab === 'options' && (
            <div className="cl-opt-cards">

              {/* Password Protection */}
              <div className="cl-opt-card">
                <div className="cl-opt-card-header">
                  <span className="cl-opt-card-icon">🔒</span>
                  <span className="cl-opt-card-title">Password Protection</span>
                  {isFree
                    ? <span className="cl-pro-tag" style={{ marginLeft: 'auto' }}>⭐ Pro</span>
                    : <span className="cl-opt-card-desc">optional</span>
                  }
                </div>
                <input
                  id="cl-password"
                  type="text"
                  className="cl-input"
                  placeholder={isFree ? 'Upgrade to add password protection' : 'Enter a password for this link'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={isFree}
                />
                {isFree
                  ? <p className="cl-opt-hint">Password protection requires a Pro plan. <Link to="/upgrade">Upgrade now</Link></p>
                  : <p className="cl-opt-hint">Visitors will be prompted to enter this password before being redirected.</p>
                }
              </div>

              {/* Expiration Date */}
              <div className="cl-opt-card">
                <div className="cl-opt-card-header">
                  <span className="cl-opt-card-icon">📅</span>
                  <span className="cl-opt-card-title">Expiration Date</span>
                  {isFree
                    ? <span className="cl-pro-tag" style={{ marginLeft: 'auto' }}>⭐ Pro</span>
                    : <span className="cl-opt-card-desc">optional</span>
                  }
                </div>
                <input
                  id="cl-expires"
                  type="date"
                  className="cl-input"
                  value={expiresAt}
                  onChange={e => setExpiresAt(e.target.value)}
                  disabled={isFree}
                  min={new Date().toISOString().split('T')[0]}
                />
                {isFree
                  ? <p className="cl-opt-hint">Link expiration requires a Pro plan. <Link to="/upgrade">Upgrade now</Link></p>
                  : <p className="cl-opt-hint">The link will automatically deactivate after this date.</p>
                }
              </div>

            </div>
          )}

          {/* ── Link preview (always visible, full-width, centered) ── */}
          {(() => {
            const p = getPreviewUrl(canUseBrand ? urlType : 'standard');
            return (
              <div className="cl-preview-box">
                <div className="cl-preview-header">
                  <span className="cl-preview-label">Your link will look like</span>
                  {!customShortCode.trim() && (
                    <button
                      type="button"
                      className="cl-regen-btn"
                      onClick={() => setPregenCode(generateCode())}
                      title="Generate a different short code"
                    >
                      ↻ New code
                    </button>
                  )}
                </div>
                <div className="cl-preview-url">
                  {p.prefix}
                  {p.brand && <span className="brand-part">{p.brand}</span>}
                  {p.middle}<span className="slug-part">{p.slug}</span>
                </div>
              </div>
            );
          })()}

          {/* ── Actions (always visible) ── */}
          <div className="cl-actions">
            <button type="submit" className="cl-submit" id="cl-submit-btn">🔗 Create Short Link</button>
            <button type="button" className="cl-back" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
          </div>

        </form>

      </div>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onConfirm={popup.onConfirm || (() => setPopup(p => ({ ...p, isOpen: false })))}
        onCancel={() => setPopup(p => ({ ...p, isOpen: false }))}
      />
    </div>
  );
};

export default CreateLink;
